import React from "react";
import NodePorts from "./NodePorts";
import { Handle, Position } from "react-flow-renderer";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import { Menu } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  nodeContainer: {
    minWidth: 200,
    maxWidth: 280,
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: "linear-gradient(135deg, #673AB7 0%, #9C27B0 100%)",
    boxShadow: "0 4px 12px rgba(103, 58, 183, 0.4)",
    border: "2px solid #673AB7",
    color: "white",
    position: "relative",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(103, 58, 183, 0.5)",
    },
  },
  nodeHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  nodeIcon: {
    fontSize: 20,
  },
  nodeLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: "white",
  },
  nodeContent: {
    fontSize: 12,
    opacity: 0.95,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 8,
  },
  optionsCount: {
    fontSize: 11,
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  handle: {
    width: 12,
    height: 12,
    background: "white",
    border: "2px solid #673AB7",
    borderRadius: "50%",
  },
}));

const MenuNode = ({ data, selected }) => {
  const classes = useStyles();
  const messageText = data?.message || data?.label || "Menú";
  const options = data?.options || [];
  const optionsCount = options.length;

  // ✅ Calcular posición de handles dinámicamente
  const getHandlePosition = (index, total) => {
    if (total === 0) return "50%"; // Sin opciones, centrado
    if (total === 1) return "50%"; // Una opción, centrado
    // Múltiples opciones: distribuir uniformemente
    const spacing = 100 / (total + 1); // Dividir en espacios iguales
    return `${spacing * (index + 1)}%`;
  };

  return (
    <Box
      className={classes.nodeContainer}
      style={{
        boxShadow: selected
          ? "0 6px 20px rgba(103, 58, 183, 0.6)"
          : "0 4px 12px rgba(103, 58, 183, 0.4)",
      }}
    >
      <NodePorts color="#673AB7" salida={false} />
      <Box className={classes.nodeHeader}>
        <Menu className={classes.nodeIcon} />
        <Typography className={classes.nodeLabel}>Menú</Typography>
      </Box>
      <Box className={classes.nodeContent}>
        {messageText.length > 40 ? `${messageText.substring(0, 40)}...` : messageText}
      </Box>
      <Typography className={classes.optionsCount}>
        {optionsCount > 0 ? `${optionsCount} opción(es)` : "Sin opciones"}
      </Typography>
      
      {/* ✅ CRÍTICO: Crear múltiples handles de salida, uno por cada opción */}
      {optionsCount > 0 ? (
        options.map((option, index) => {
          const optionValue = option.value || option.text || `option${index + 1}`;
          const optionText = option.text || `Opción ${index + 1}`;
          const handlePosition = getHandlePosition(index, optionsCount);
          
          return (
            <Handle
              key={`source-${optionValue}`}
              type="source"
              position={Position.Bottom}
              id={optionValue} // ✅ CRÍTICO: El id debe ser igual al 'value' de la opción
              className={classes.handle}
              style={{
                bottom: -6,
                left: handlePosition,
                transform: "translateX(-50%)",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  bottom: -20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: 9,
                  fontWeight: 600,
                  color: "#673AB7",
                  backgroundColor: "white",
                  padding: "2px 4px",
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                  maxWidth: 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={optionText}
              >
                {optionText.length > 12 ? `${optionText.substring(0, 12)}...` : optionText}
              </Box>
            </Handle>
          );
        })
      ) : (
        // ✅ Si no hay opciones, mostrar handle por defecto (sin id específico)
        <Handle
          type="source"
          position={Position.Bottom}
          className={classes.handle}
          style={{ bottom: -6 }}
        />
      )}
    </Box>
  );
};

export default MenuNode;
